import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Factory, FileText, CheckCircle, GitBranch, ShoppingCart, Shirt, Building2, Database, Package, Workflow } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import RelatedSolutionsSection from "@/components/RelatedSolutionsSection";

const Manufacturing = () => {
  useSEO({
    title: 'Manufacturing Operations Management | Ommni',
    description: 'Manage BOMs, product configurators, and supply chain for manufacturers. Track materials, components, and finished goods with Ommni manufacturing solutions.',
    keywords: 'manufacturing operations, BOM management, supply chain management, manufacturing software, product configurator',
    canonical: 'https://ommni.io/solutions/manufacturing',
  });

  const features = [
    {
      icon: FileText,
      title: "Bill of Materials Management",
      description: "Centralize and version-control your BOMs with complete traceability from raw materials to finished goods.",
    },
    {
      icon: CheckCircle,
      title: "Quality Control Integration",
      description: "Track quality metrics and ensure compliance across your entire production workflow.",
    },
    {
      icon: GitBranch,
      title: "Production Data Flow",
      description: "Seamlessly connect production systems to downstream distribution and retail channels.",
    },
    {
      icon: Factory,
      title: "Multi-Site Coordination",
      description: "Manage product data across multiple manufacturing facilities and distribution centers.",
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Manufacturing Operations Management",
    "description": "Streamline product data from production to distribution with intelligent workflows. Manage BOMs, product configurators, quality control, and supply chain coordination.",
    "serviceType": "Manufacturing Operations Management",
    "provider": {
      "@type": "Organization",
      "name": "Ommni",
      "url": "https://ommni.io"
    },
    "areaServed": {
      "@type": "Place",
      "name": "Global"
    },
    "url": "https://ommni.io/solutions/manufacturing"
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-32 px-4 pb-24">
        {/* JSON-LD Structured Data for Service */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
        
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Factory className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Manufacturing Solutions
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Streamline product data from production to distribution with intelligent workflows
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="cta" size="lg" asChild>
                <Link to="/contact">Get Started</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <Card key={index} className="p-8 gradient-card">
                <div className="mb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </Card>
            ))}
          </div>

          {/* Powered By Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-4">
              Powered By Ommni Features
            </h2>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              Manufacturing solutions built on our core platform capabilities
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Database className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/pim" className="hover:text-primary transition-colors">
                    Product Information Management
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Manage BOMs and technical specifications from production to distribution.
                </p>
              </Card>
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Package className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/inventory" className="hover:text-primary transition-colors">
                    Inventory Management
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Track materials, components, and finished goods across production facilities.
                </p>
              </Card>
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Workflow className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/ommni-flow" className="hover:text-primary transition-colors">
                    Workflow Automation
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Automate production workflows and data flows to downstream channels.
                </p>
              </Card>
            </div>
          </div>

          {/* Related Solutions */}
          <RelatedSolutionsSection
            solutions={[
              {
                icon: Building2,
                title: "Wholesale & Distribution",
                description: "B2B operations with automated workflows.",
                link: "/solutions/wholesale",
                gradient: "bg-gradient-to-br from-purple-500/20 to-pink-500/20",
              },
              {
                icon: ShoppingCart,
                title: "E-commerce & Retail",
                description: "Multi-channel retail operations.",
                link: "/solutions/ecommerce",
                gradient: "bg-gradient-to-br from-blue-500/20 to-cyan-500/20",
              },
              {
                icon: Shirt,
                title: "Fashion & Apparel",
                description: "Complex variant management for fashion.",
                link: "/solutions/fashion",
                gradient: "bg-gradient-to-br from-pink-500/20 to-rose-500/20",
              },
            ]}
          />

          {/* Benefits Section */}
          <div className="relative overflow-hidden rounded-lg border-2 bg-card p-12">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-purple-500/5 to-background"></div>
            <div className="relative z-10">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Accelerate Time-to-Market
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Reduce product launch cycles by ensuring accurate data flows from production to all distribution channels
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">40%</div>
                  <p className="text-muted-foreground">Faster Product Launches</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">60%</div>
                  <p className="text-muted-foreground">Fewer Data Entry Errors</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">100%</div>
                  <p className="text-muted-foreground">Product Traceability</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default Manufacturing;
